// Structured data schemas for SEO

export const getWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Utility Tools",
  "alternateName": ["PDF Converter", "Online Tools", "File Converter"],
  "url": window.location.origin,
  "description": "Free online utility tools including PDF converter, document processor, and productivity enhancers. Convert files instantly in your browser.",
  "author": {
    "@type": "Person",
    "name": "nkcodescribbler"
  },
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${window.location.origin}/?q={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
});

export const getPDFConverterSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "PDF Converter Tool",
  "description": "Convert images, documents, and text files to PDF format instantly in your browser. Supports batch processing, compression, and merge options.",
  "url": `${window.location.origin}/#converter`,
  "applicationCategory": "ProductivityApplication",
  "operatingSystem": "Web Browser",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock"
  },
  "author": {
    "@type": "Person",
    "name": "nkcodescribbler"
  },
  "features": [
    "Convert multiple file formats to PDF",
    "Batch file processing",
    "Merge multiple files into one PDF",
    "Adjustable compression settings",
    "Browser-based processing (no file upload)",
    "Supports images, documents, spreadsheets, presentations"
  ],
  "supportedMedia": [
    "image/jpeg",
    "image/png", 
    "image/gif",
    "image/bmp",
    "image/tiff",
    "image/webp",
    "text/plain",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "text/html",
    "text/markdown"
  ],
  "browserRequirements": "HTML5, JavaScript enabled"
});

export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Utility Tools",
  "url": window.location.origin,
  "description": "Providing free online utility tools for productivity and file processing.",
  "founder": {
    "@type": "Person",
    "name": "nkcodescribbler"
  },
  "sameAs": [
    "https://facebook.com",
    "https://twitter.com",
    "https://linkedin.com"
  ]
});

export const getBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `${window.location.origin}${item.url}`
  }))
});

export const getFAQSchema = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Is the PDF converter free to use?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, our PDF converter is completely free to use with no hidden charges or subscription required."
      }
    },
    {
      "@type": "Question", 
      "name": "Are my files uploaded to your servers?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No, all file processing happens directly in your browser. Your files never leave your device, ensuring complete privacy and security."
      }
    },
    {
      "@type": "Question",
      "name": "What file formats can I convert to PDF?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "You can convert images (JPG, PNG, GIF, BMP, TIFF, WebP), text files (TXT), documents (DOC, DOCX), spreadsheets (XLS, XLSX), presentations (PPT, PPTX), web files (HTML), and Markdown (MD) to PDF."
      }
    },
    {
      "@type": "Question",
      "name": "What is the maximum file size I can convert?", 
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The maximum file size is 10MB per file. You can convert multiple files in a single batch operation."
      }
    },
    {
      "@type": "Question",
      "name": "Can I merge multiple files into one PDF?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, you can select multiple files and choose the merge option to combine them into a single PDF document."
      }
    }
  ]
});

// Schema for individual tools (extensible for future tools)
export const getToolSchema = (toolName, toolDescription, toolUrl, features = []) => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": toolName,
  "description": toolDescription,
  "url": toolUrl,
  "applicationCategory": "UtilityApplication",
  "operatingSystem": "Any",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "features": features,
  "author": {
    "@type": "Person",
    "name": "nkcodescribbler"
  }
});

export const getFeedbackPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": "Utility Tools Feedback",
  "description": "Share your feedback, suggestions, and report issues to help us improve our utility tools and services.",
  "url": `${window.location.origin}/#feedback`,
  "mainEntity": {
    "@type": "Organization",
    "name": "Utility Tools",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer support",
      "email": "info@navasakthienterprise.in",
      "availableLanguage": "English"
    }
  },
  "potentialAction": {
    "@type": "CommunicateAction",
    "name": "Submit Feedback",
    "description": "Submit feedback, suggestions, or report issues about our utility tools"
  }
});

export const getFeedbackFormSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "Feedback Form",
  "description": "Interactive feedback form for collecting user suggestions, bug reports, and feature requests for Utility Tools",
  "url": `${window.location.origin}/#feedback`,
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Any",
  "features": [
    "Star rating system",
    "Categorized feedback types",
    "Math captcha verification",
    "Instant email delivery",
    "Mobile responsive design"
  ],
  "author": {
    "@type": "Person",
    "name": "nkcodescribbler"
  }
});

export const getPDFPageRemoverSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "PDF Page Remover Tool",
  "description": "Remove specific pages from PDF documents instantly in your browser. Remove ranges, specific pages, or keep only selected pages. No file upload required - complete privacy.",
  "url": `${window.location.origin}/#page-remover`,
  "applicationCategory": "ProductivityApplication",
  "operatingSystem": "Web Browser",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock"
  },
  "author": {
    "@type": "Person",
    "name": "nkcodescribbler"
  },
  "features": [
    "Remove specific page ranges from PDF",
    "Remove individual pages by number",
    "Keep only selected pages (remove all others)",
    "Browser-based processing (no file upload)",
    "Preview pages before removal",
    "Batch processing support",
    "Instant download of modified PDF"
  ],
  "supportedMedia": [
    "application/pdf"
  ],
  "browserRequirements": "HTML5, JavaScript enabled, PDF.js support"
});

export const getPrivacyPolicySchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Privacy Policy",
  "description": "Privacy Policy for Utility Tools PDF Converter - Learn how we protect your data and privacy in compliance with Indian privacy laws",
  "url": `${window.location.origin}/#privacy`,
  "about": {
    "@type": "Organization",
    "name": "Utility Tools",
    "founder": {
      "@type": "Person",
      "name": "nkcodescribbler"
    }
  },
  "mainEntity": {
    "@type": "PrivacyPolicy",
    "name": "Utility Tools Privacy Policy",
    "description": "Comprehensive privacy policy detailing data protection practices in compliance with DPDP Act 2023 and IT Act 2000",
    "dateModified": "2025-09-10",
    "datePublished": "2025-09-10",
    "publisher": {
      "@type": "Organization",
      "name": "Utility Tools"
    }
  }
});